import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const SearchDropdown = ({
    searchQuery,
    searchResults,
    searchSuggestions,
    searchHistory,
    isSearching,
    searchError,
    showDropdown,
    onSuggestionClick,
    onProductClick,
    onHistoryClick,
    onClearHistory,
    onClose,
    className = '',
    maxHeight = 'max-h-96'
}) => {
    const navigate = useNavigate();

    // Default handlers if not provided
    const handleSuggestionClick = onSuggestionClick || ((suggestion) => {
        navigate('/gift-finder', { state: { query: suggestion } });
    });

    const handleProductClick = onProductClick || ((product) => {
        navigate(`/products/${product.slug}`, { state: { product } });
    });

    const handleHistoryClick = onHistoryClick || ((historyItem) => {
        navigate('/gift-finder', { state: { query: historyItem } });
    });

    if (!showDropdown) return null;

    return (
        <div className={`absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-accent-100 z-50 ${maxHeight} overflow-y-auto ${className}`}>
            {/* Search Error */}
            {searchError && (
                <div className="p-4 text-sm text-red-600 bg-red-50 border-b border-red-100">
                    {searchError}
                </div>
            )}

            {/* Search Results */}
            {searchResults.length > 0 && (
                <div className="border-b border-accent-100">
                    <div className="p-3 bg-accent-50">
                        <h4 className="text-sm font-medium text-accent-700 flex items-center">
                            <Icon name="Package" size={14} className="mr-2" />
                            Products ({searchResults.length})
                        </h4>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                        {searchResults.map((product) => (
                            <button
                                key={product.id}
                                onClick={() => handleProductClick(product)}
                                className="w-full flex items-center space-x-3 p-3 hover:bg-accent-50 transition-colors text-left"
                            >
                                {product.images && product.images.length > 0 && (
                                    <img
                                        src={product.images[0].image || product.images[0]}
                                        alt={product.name}
                                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-accent-800 truncate">
                                        {product.name || product.title}
                                    </p>
                                    <p className="text-xs text-accent-600">
                                        {product.category?.name} • ${product.retail_price || product.price}
                                    </p>
                                </div>
                                <Icon name="ArrowRight" size={14} className="text-accent-400" />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Search Suggestions */}
            {searchSuggestions.length > 0 && (
                <div className="border-b border-accent-100">
                    <div className="p-3 bg-accent-50">
                        <h4 className="text-sm font-medium text-accent-700 flex items-center">
                            <Icon name="Lightbulb" size={14} className="mr-2" />
                            Suggestions
                        </h4>
                    </div>
                    <div>
                        {searchSuggestions.map((suggestion, index) => (
                            <button
                                key={index}
                                onClick={() => handleSuggestionClick(suggestion)}
                                className="w-full flex items-center space-x-3 p-3 hover:bg-accent-50 transition-colors text-left"
                            >
                                <Icon name="Search" size={14} className="text-accent-400" />
                                <span className="text-sm text-accent-700">{suggestion}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Search History */}
            {searchHistory.length > 0 && searchQuery.length === 0 && (
                <div>
                    <div className="p-3 bg-accent-50 flex items-center justify-between">
                        <h4 className="text-sm font-medium text-accent-700 flex items-center">
                            <Icon name="Clock" size={14} className="mr-2" />
                            Recent Searches
                        </h4>
                        {onClearHistory && (
                            <button
                                onClick={onClearHistory}
                                className="text-xs text-accent-500 hover:text-accent-700"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    <div>
                        {searchHistory.map((historyItem, index) => (
                            <button
                                key={index}
                                onClick={() => handleHistoryClick(historyItem)}
                                className="w-full flex items-center space-x-3 p-3 hover:bg-accent-50 transition-colors text-left"
                            >
                                <Icon name="RotateCcw" size={14} className="text-accent-400" />
                                <span className="text-sm text-accent-700">{historyItem}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* No Results */}
            {searchQuery.length > 0 && !isSearching && searchResults.length === 0 && searchSuggestions.length === 0 && !searchError && (
                <div className="p-6 text-center">
                    <Icon name="Search" size={32} className="text-accent-300 mx-auto mb-3" />
                    <p className="text-sm text-accent-600 mb-2">No results found for "{searchQuery}"</p>
                    <p className="text-xs text-accent-500">Try different keywords or browse our categories</p>
                </div>
            )}

            {/* Loading State */}
            {isSearching && (
                <div className="p-6 text-center">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-sm text-accent-600">Searching...</p>
                </div>
            )}

            {/* Empty State - Show when no query and no history */}
            {searchQuery.length === 0 && searchHistory.length === 0 && !isSearching && (
                <div className="p-6 text-center">
                    <Icon name="Search" size={32} className="text-accent-300 mx-auto mb-3" />
                    <p className="text-sm text-accent-600 mb-2">Start typing to search</p>
                    <p className="text-xs text-accent-500">Find gifts, brands, or categories</p>
                </div>
            )}

            {/* Quick Actions Footer */}
            <div className="p-3 bg-accent-50 border-t border-accent-100">
                <div className="flex items-center justify-between text-xs text-accent-600">
                    <span>Press Enter to search all products</span>
                    <span>ESC to close</span>
                </div>
            </div>
        </div>
    );
};

export default SearchDropdown; 